// src/pages/home/Gallery.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { FaArrowLeft, FaArrowRight, FaMinus, FaPlay, FaPlus, FaTimes, FaUndo } from "react-icons/fa";

const isExternalUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());
const isDirectVideoFile = (url = "") =>
  /\.(mp4|webm|mov|avi|m4v|ogv|ogg)(?:[?#].*)?$/i.test(String(url || "").trim());

const extractYouTubeId = (rawUrl = "") => {
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (host === "youtu.be") {
      return url.pathname.split("/").filter(Boolean)[0] || "";
    }
    if (host.includes("youtube.com")) {
      if (url.pathname === "/watch") return url.searchParams.get("v") || "";
      const parts = url.pathname.split("/").filter(Boolean);
      if (["embed", "shorts", "live"].includes(parts[0])) return parts[1] || "";
    }
  } catch (_err) {
    return "";
  }
  return "";
};

const toYouTubeEmbedUrl = (rawUrl = "") => {
  const id = extractYouTubeId(rawUrl);
  return id ? `https://www.youtube.com/embed/${id}` : "";
};

const getBaseTitle = (title) => {
  if (!title) return "";
  if (/\.(jpg|jpeg|png|webp|gif|mp4|mov|avi|webm)$/i.test(title)) {
    return "";
  }
  return title.replace(/\s*\(\d+\)$/, "").trim();
};

const toVimeoEmbedUrl = (rawUrl = "") => {
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (!host.includes("vimeo.com")) return "";
    const parts = url.pathname.split("/").filter(Boolean);
    if (!parts.length) return "";
    const id = parts[0] === "video" ? parts[1] : parts[0];
    return /^\d+$/.test(id || "") ? `https://player.vimeo.com/video/${id}` : "";
  } catch (_err) {
    return "";
  }
};

const toEmbedUrl = (rawUrl = "") => toYouTubeEmbedUrl(rawUrl) || toVimeoEmbedUrl(rawUrl);
const normalizeCategory = (value) => String(value || "").trim().toLowerCase();
const hostLabel = (rawUrl = "") => {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./i, "");
  } catch (_err) {
    return "External link";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const toDateKey = (value) => {
  if (!value) return "unknown-date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown-date";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameLocalDate = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDateHeading = (dateKey) => {
  if (dateKey === "unknown-date") return "Unknown date";
  const [year, month, day] = dateKey.split("-").map(Number);
  const sectionDate = new Date(year, month - 1, day);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameLocalDate(sectionDate, today)) return "Today";
  if (isSameLocalDate(sectionDate, yesterday)) return "Yesterday";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(sectionDate);
};

const formatDateSubheading = (dateKey) => {
  if (dateKey === "unknown-date") return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isClosingVideo, setIsClosingVideo] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [activeFilter, setActiveFilter] = useState("all");
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.25;

  // Load gallery media
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await API.get("/gallery");
        const list = Array.isArray(res) ? res : [];
        setItems(list);
      } catch (err) {
        console.error("Failed to load gallery", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const photos = useMemo(
    () => {
      const list = items.filter((item) => String(item?.file_type || "").toLowerCase() === "image");
      return list.sort((a, b) => {
        const dateA = a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
        const dateB = b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
        return dateB - dateA;
      });
    },
    [items]
  );
  const videos = useMemo(
    () => items.filter((item) => String(item?.file_type || "").toLowerCase() === "video"),
    [items]
  );
  const playerVideos = useMemo(() => {
    const tagged = videos.filter((item) => normalizeCategory(item?.category) === "player_videos");
    return tagged.length ? tagged : videos;
  }, [videos]);

  const selectedIndex = useMemo(
    () => (selectedImage ? photos.findIndex((img) => img.id === selectedImage.id) : -1),
    [photos, selectedImage]
  );

  const filteredItems = useMemo(() => {
    let list = [];
    if (activeFilter === "all") {
      list = [...photos, ...playerVideos];
    } else if (activeFilter === "photos") {
      list = photos;
    } else if (activeFilter === "videos") {
      list = playerVideos;
    }
    return list.sort((a, b) => {
      const dateA = a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
      const dateB = b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [photos, playerVideos, activeFilter]);

  const groupedMedia = useMemo(() => {
    const byDate = filteredItems.reduce((acc, item) => {
      const dateKey = toDateKey(item?.uploaded_at);
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(item);
      return acc;
    }, {});

    const result = {};
    Object.keys(byDate).forEach((dateKey) => {
      const itemsForDate = byDate[dateKey];
      const groups = [];
      let untitledGroup = null;
      const groupMap = {};

      itemsForDate.forEach((item) => {
        const title = item?.title || "";
        const baseTitle = getBaseTitle(title);

        if (!baseTitle) {
          if (!untitledGroup) {
            untitledGroup = {
              baseTitle: "",
              items: []
            };
            groups.push(untitledGroup);
          }
          untitledGroup.items.push(item);
        } else {
          if (groupMap[baseTitle] !== undefined) {
            groups[groupMap[baseTitle]].items.push(item);
          } else {
            const newGroup = {
              baseTitle,
              items: [item]
            };
            groups.push(newGroup);
            groupMap[baseTitle] = groups.length - 1;
          }
        }
      });
      result[dateKey] = groups;
    });

    return result;
  }, [filteredItems]);

  const sortedDateKeys = useMemo(() => {
    return Object.keys(groupedMedia).sort((a, b) => {
      if (a === "unknown-date") return 1;
      if (b === "unknown-date") return -1;
      return b.localeCompare(a); // Sort descending (newest dates first)
    });
  }, [groupedMedia]);

  const closeImage = () => {
    setSelectedImage(null);
    setImageZoom(1);
  };
  const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
  const increaseZoom = () => setImageZoom((prev) => clampZoom(prev + ZOOM_STEP));
  const decreaseZoom = () => setImageZoom((prev) => clampZoom(prev - ZOOM_STEP));
  const resetZoom = () => setImageZoom(1);
  const openVideo = (videoItem) => {
    if (!videoItem) return;
    setIsClosingVideo(false);
    setSelectedVideo(videoItem);
  };
  const closeVideo = () => {
    if (!selectedVideo || isClosingVideo) return;
    setIsClosingVideo(true);
    window.setTimeout(() => {
      setSelectedVideo(null);
      setIsClosingVideo(false);
    }, 220);
  };

  const goPrev = (e) => {
    e?.stopPropagation();
    if (selectedIndex === -1 || photos.length === 0) return;
    const prevIndex = (selectedIndex - 1 + photos.length) % photos.length;
    setSelectedImage(photos[prevIndex]);
    setImageZoom(1);
  };

  const goNext = (e) => {
    e?.stopPropagation();
    if (selectedIndex === -1 || photos.length === 0) return;
    const nextIndex = (selectedIndex + 1) % photos.length;
    setSelectedImage(photos[nextIndex]);
    setImageZoom(1);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!selectedImage && !selectedVideo) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (selectedVideo) closeVideo();
        else closeImage();
        return;
      }
      if (selectedVideo) return;
      if (event.key === "ArrowLeft") goPrev(event);
      else if (event.key === "ArrowRight") goNext(event);
      else if (event.key === "+" || event.key === "=") increaseZoom();
      else if (event.key === "-" || event.key === "_") decreaseZoom();
      else if (event.key === "0") resetZoom();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, selectedVideo, selectedIndex, photos.length, isClosingVideo]);

  return (
    <>
      <Navbar />

      <main className="bg-slate-50">
        {/* ===== HERO SECTION ===== */}
        <section className="bg-white py-16 text-center border-b border-slate-200">
          <div className="max-w-screen-lg mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">Our Gallery</h1>
            <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
              Photos and player videos capturing moments from our journey of triumph and resilience.
            </p>
          </div>
        </section>

        {/* ===== FILTER NAVIGATION ===== */}
        <section className="bg-slate-100 border-b border-slate-200 py-6">
          <div className="max-w-screen-xl mx-auto px-4 flex justify-center gap-3">
            {[
              { id: "all", label: "All Media", count: photos.length + playerVideos.length },
              { id: "photos", label: "Photos Only", count: photos.length },
              { id: "videos", label: "Videos Only", count: playerVideos.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 border flex items-center gap-2 ${
                  activeFilter === tab.id
                    ? "bg-purple-600 border-purple-600 text-white shadow-lg shadow-purple-100 scale-105"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                }`}
              >
                <span>{tab.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeFilter === tab.id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* ===== UNIFIED GALLERY GRID ===== */}
        <section className="py-12 px-4">
          <div className="max-w-screen-xl mx-auto">
            {loading ? (
              <div className="text-center text-slate-500">Loading gallery...</div>
            ) : sortedDateKeys.length === 0 ? (
              <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">
                No media files found matching the filter.
              </div>
            ) : (
              <div className="space-y-12">
                {sortedDateKeys.map((dateKey) => (
                  <div key={dateKey} className="border-b border-slate-100 pb-8 last:border-0 last:pb-0">
                    {/* Sticky Date Header block */}
                    <div className="sticky top-[64px] z-10 mb-6 bg-slate-50/95 py-2.5 border-b border-slate-200 backdrop-blur flex items-end justify-between">
                      <div>
                        <h3 className="text-base font-bold text-slate-900">
                          {formatDateHeading(dateKey)}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {formatDateSubheading(dateKey)}
                        </p>
                      </div>
                    </div>

                    {/* Group by base titles inside this date */}
                    <div className="space-y-8">
                      {groupedMedia[dateKey].map((group, groupIdx) => (
                        <div key={groupIdx}>
                          {group.baseTitle && (
                            <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">
                              {group.baseTitle}
                            </h4>
                          )}
                          
                          {/* Mixed Media Grid */}
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                            {group.items.map((item) => {
                              const isImage = String(item?.file_type || "").toLowerCase() === "image";
                              
                              if (isImage) {
                                return (
                                  <button
                                    key={item.id}
                                    onClick={() => {
                                      setSelectedImage(item);
                                      setImageZoom(1);
                                    }}
                                    className="relative aspect-video block w-full overflow-hidden rounded-xl shadow-md group transform transition-transform duration-300 ease-in-out hover:scale-105 border border-slate-200 bg-white"
                                  >
                                    <img
                                      src={resolveAssetUrl(item.url || item.file_path)}
                                      alt={item.title || "Gallery image"}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                    <div className="absolute inset-0 p-4 bg-gradient-to-t from-black/85 via-black/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end text-left">
                                      <p className="text-[11px] font-medium text-slate-300 mt-1.5 flex items-center gap-1">
                                        <span>📅</span> {formatDate(item.uploaded_at)}
                                      </p>
                                    </div>
                                  </button>
                                );
                              } else {
                                const rawPath = String(item?.url || item?.file_path || "").trim();
                                const mediaUrl = resolveAssetUrl(rawPath, { folder: "gallery" });
                                const isExternalVideo = isExternalUrl(rawPath);
                                const embedUrl = isExternalVideo ? toEmbedUrl(mediaUrl) : "";
                                const isLinkCard = isExternalVideo && !embedUrl && !isDirectVideoFile(mediaUrl);
                                const ytId = extractYouTubeId(rawPath);
                                const isYouTube = !!ytId;
                                const ytThumbnail = isYouTube ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : "";

                                const videoPayload = {
                                  id: item.id,
                                  title: item.title || "Player video",
                                  mediaUrl,
                                  embedUrl,
                                  isLinkCard,
                                  isExternalVideo,
                                };

                                return (
                                  <div
                                    key={item.id}
                                    className="relative aspect-video block w-full overflow-hidden rounded-xl shadow-md group transform transition-transform duration-300 ease-in-out hover:scale-105 border border-slate-200 bg-white"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openVideo(videoPayload)}
                                      className="w-full h-full text-left relative block"
                                      aria-label={`Play ${item.title || "player video"}`}
                                    >
                                      {isYouTube ? (
                                        <img
                                          src={ytThumbnail}
                                          alt={item.title || "YouTube video"}
                                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                          loading="lazy"
                                        />
                                      ) : isLinkCard ? (
                                        <div className="h-full w-full flex flex-col items-center justify-center gap-2 px-4 text-center bg-slate-900 text-white">
                                          <FaPlay className="text-2xl text-purple-400" />
                                          <p className="text-xs font-semibold">External video link</p>
                                          <p className="text-[10px] text-slate-400">{hostLabel(mediaUrl)}</p>
                                        </div>
                                      ) : (
                                        <video
                                          src={mediaUrl}
                                          preload="metadata"
                                          muted
                                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                      )}
                                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/95 text-purple-600 shadow-md transform group-hover:scale-110 transition-transform">
                                          <FaPlay size={12} className="ml-0.5" />
                                        </span>
                                      </div>
                                    </button>
                                  </div>
                                );
                              }
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <PageSectionsRenderer pageKey="gallery" />
      </main>
      
      {/* ===== LIGHTBOX MODAL ===== */}
      {selectedImage && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md animate-fade-in"
          onClick={closeImage}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={closeImage}
            aria-label="Close image viewer"
            className="absolute top-4 right-4 z-10 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={decreaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom out"
            >
              <FaMinus size={14} />
            </button>
            <button
              type="button"
              onClick={increaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom in"
            >
              <FaPlus size={14} />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Reset zoom"
            >
              <FaUndo size={14} />
            </button>
          </div>

          {/* Previous Button */}
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous image"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors"
          >
            <FaArrowLeft size={24} />
          </button>

          {/* Next Button */}
          <button
            type="button"
            onClick={goNext}
            aria-label="Next image"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors"
          >
            <FaArrowRight size={24} />
          </button>

          <div
            className="relative max-w-5xl w-full max-h-[92vh] p-2 sm:p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl overflow-hidden bg-black/80">
              <div
                className="flex items-center justify-center p-2 sm:p-3 bg-black overflow-auto"
                onWheel={(event) => {
                  event.preventDefault();
                  if (event.deltaY < 0) {
                    increaseZoom();
                  } else {
                    decreaseZoom();
                  }
                }}
              >
                <img
                  src={resolveAssetUrl(selectedImage.url || selectedImage.file_path)}
                  alt={selectedImage.title || "Selected image"}
                  className="max-h-[78vh] w-auto max-w-full object-contain"
                  style={{
                    transform: `scale(${imageZoom})`,
                    transition: "transform 120ms ease-out",
                  }}
                />
              </div>
              <div className="px-5 py-4 text-center bg-slate-900/95 border-t border-white/10">
                {getBaseTitle(selectedImage.title) && (
                  <p className="text-white text-lg font-bold tracking-tight">
                    {getBaseTitle(selectedImage.title)}
                  </p>
                )}
                <p className="text-slate-400 text-xs mt-1.5 flex items-center justify-center gap-1">
                  <span>📅</span> {formatDate(selectedImage.uploaded_at)}
                  {photos.length > 1 && (
                    <span className="ml-2 bg-slate-800 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                      {selectedIndex + 1} of {photos.length}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== VIDEO MODAL ===== */}
      {selectedVideo && (
        <div
          role="dialog"
          aria-modal="true"
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 ${isClosingVideo ? "animate-fade-out" : "animate-fade-in"}`}
          onClick={closeVideo}
        >
          <button
            onClick={closeVideo}
            aria-label="Close video player"
            className="absolute top-4 right-4 z-10 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div
            className="relative max-w-5xl w-full max-h-[85vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl overflow-hidden bg-black">
              {selectedVideo.embedUrl ? (
                <iframe
                  src={selectedVideo.embedUrl}
                  title={selectedVideo.title || "Player video"}
                  className="w-full aspect-video"
                  allow="autoplay; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : selectedVideo.isLinkCard ? (
                <div className="w-full aspect-video flex flex-col items-center justify-center gap-3 text-white px-5 text-center">
                  <FaPlay className="text-3xl" />
                  <p className="font-semibold">This video opens on an external site.</p>
                  <a
                    href={selectedVideo.mediaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 rounded bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
                  >
                    Open Video Link
                  </a>
                </div>
              ) : (
                <video
                  src={selectedVideo.mediaUrl}
                  controls
                  autoPlay
                  className="w-full max-h-[75vh] object-contain"
                />
              )}
            </div>
            {selectedVideo.title && (
              <div className="mt-3 text-center">
                <p className="text-white text-lg font-semibold bg-black/30 inline-block px-4 py-2 rounded-lg">
                  {selectedVideo.title}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
       <style>{`
        @keyframes fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes fade-out {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        .animate-fade-out {
          animation: fade-out 0.22s ease-in forwards;
        }
      `}</style>
    </>
  );
};

export default Gallery;
